import {
  memo,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  useState,
} from "react";
import { useMobile } from "../../utils/mobile";

const SingleOTPInput = (props) => {
  const usePrevious = (value) => {
    const ref = useRef();

    // Store current value in ref
    useEffect(() => {
      ref.current = value;
    }, [value]); // Only re-run if value changes

    // Return previous value (happens before update in useEffect above)
    return ref.current;
  };

  const { focus, autoFocus, ...rest } = props;
  const inputRef = useRef(null);
  const prevFocus = usePrevious(!!focus);
  const isMobile = useMobile();

  useLayoutEffect(() => {
    if (inputRef.current) {
      if (focus && autoFocus) {
        inputRef.current.focus();
      }
      if (focus && autoFocus && focus !== prevFocus) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [autoFocus, focus, prevFocus]);

  return <input
    ref={inputRef}
    {...rest}
    type="tel"
    inputMode="numeric"
    // Lets iOS/Safari offer the code straight from the SMS. The digits are
    // shown, not masked: a one-time code is short-lived, and hiding it only
    // makes typing mistakes harder to spot.
    autoComplete="one-time-code"
    style={{
      ...rest.style,
      fontSize: isMobile ? 28 : 40,
      textAlign: "center",
    }}
  />
};

const OTPInput = (props) => {
  const {
    length,
    isNumberInput,
    autoFocus,
    disabled,
    onChangeOTP,
    inputClassName,
    inputStyle,
    ...rest
  } = props;

  // Define state activeInput = 0
  const [activeInput, setActiveInput] = useState(0);

  // Define state otpValues = array with <length> items with default value = ""
  const [otpValues, setOTPValues] = useState(Array(length).fill(""));

  // helpers
  // Define some helper functions
  const focusInput = useCallback(
    (inputIndex) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length]
  );

  const getRightValue = useCallback(
    (str) => {
      let changedValue = str;

      if (!isNumberInput || !changedValue) {
        return changedValue;
      }

      return Number(changedValue) >= 0 ? changedValue : "";
    },
    [isNumberInput]
  );

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  // Handle onFocus input
  const handleOnFocus = useCallback(
    (index) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  const handleOtpChange = useCallback(
    (otp) => {
      const otpValue = otp.join("");
      onChangeOTP(otpValue);
    },
    [onChangeOTP]
  );

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const changeCodeAtFocus = useCallback(
    (str) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || "";
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  // Spread a whole code over the fields, starting at `startIndex`. Shared by
  // paste and by autofill (which drops the full code into a single field).
  const fillValuesFrom = useCallback(
    (startIndex, chars) => {
      const remaining = [...chars];
      const updatedOTPValues = [...otpValues];
      let nextFocusIndex = startIndex;
      updatedOTPValues.forEach((val, index) => {
        if (index >= startIndex) {
          const changedValue = getRightValue(remaining.shift() || val);
          if (changedValue) {
            updatedOTPValues[index] = changedValue;
            nextFocusIndex = index;
          }
        }
      });
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
      setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
    },
    [getRightValue, handleOtpChange, length, otpValues]
  );

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    (e) => {
      const val = getRightValue(e.currentTarget.value);
      if (!val) {
        e.preventDefault();
        return;
      }
      if (val.length > 1) {
        // One-time-code autofill fills the whole code into one field.
        fillValuesFrom(activeInput, val.slice(0, length - activeInput).split(""));
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [
      activeInput,
      changeCodeAtFocus,
      fillValuesFrom,
      focusNextInput,
      getRightValue,
      length,
    ]
  );

  // Hanlde onBlur input

  const handleOnBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  // Handle onKeyDown input

  const handleOnKeyDown = useCallback(
    (e) => {
      const pressedKey = e.key;

      switch (pressedKey) {
        case "Backspace":
        case "Delete": {
          e.preventDefault();
          if (otpValues[activeInput]) {
            changeCodeAtFocus("");
          } else {
            focusPrevInput();
          }
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          focusNextInput();
          break;
        }
        default: {
          // Ignore all special keys if it is not numeric or alphabet characters
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault();
          }

          break;
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );

  // Handle onPaste input
  const handleOnPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text/plain")
        .trim()
        .slice(0, length - activeInput)
        .split("");
      if (pastedData) {
        fillValuesFrom(activeInput, pastedData);
      }
    },
    [activeInput, fillValuesFrom, length]
  );

  return (
    <div
      {...rest}
      style={{
        direction: "ltr",
      }}
    >
      {Array(length)
        .fill("")
        .map((_, index) => (
          <SingleOTPInput
            key={`SingleInput-${index}`}
            focus={activeInput === index}
            value={otpValues && otpValues[index]}
            autoFocus={autoFocus}
            onFocus={handleOnFocus(index)}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            onBlur={handleOnBlur}
            onPaste={handleOnPaste}
            style={inputStyle}
            disabled={disabled}
          />
        ))}
    </div>
  );
};

export default memo(OTPInput);
